import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import config from '../../config/apiConfig';
import {
  Camera, Upload, Sparkles, Check, X, Save, AlertTriangle,
  Loader, Trash2, Image as ImageIcon,
  Smartphone, FileText
} from 'lucide-react';

var API_BASE = (config.api_url || "http://localhost:5000").replace(/\/$/, "");

function ImportarCarta() {
  var slugParam = (useParams() || {}).slug;
  var [slug, setSlug] = useState(slugParam || '');
  var [step, setStep] = useState('upload'); // upload | preview | review | done
  var [file, setFile] = useState(null);
  var [previewUrl, setPreviewUrl] = useState(null);
  var [loading, setLoading] = useState(false);
  var [cargandoMensaje, setCargandoMensaje] = useState('');
  var [productos, setProductos] = useState([]);
  var [errores, setErrores] = useState([]);
  var [resultadoGuardado, setResultadoGuardado] = useState(null);
  var [token, setToken] = useState(localStorage.getItem('token') || '');
  var [usuario, setUsuario] = useState(
    JSON.parse(localStorage.getItem('usuario') || '{}')
  );
  var [cameraActive, setCameraActive] = useState(false);
  var [errorPDF, setErrorPDF] = useState(false);
  var videoRef = useRef(null);
  var canvasRef = useRef(null);
  var fileInputRef = useRef(null);
  var streamRef = useRef(null);

  useEffect(function () {
    return function () {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(function (t) { t.stop(); });
      }
    };
  }, []);

  var startCamera = useCallback(async function () {
    try {
      var stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      streamRef.current = stream;
      setCameraActive(true);
    } catch (err) {
      alert('No se pudo acceder a la cámara. Verifica los permisos.');
      console.error('Error cámara:', err);
    }
  }, []);

  var stopCamera = useCallback(function () {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(function (t) { t.stop(); });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  }, []);

  var takePhoto = useCallback(function () {
    if (!videoRef.current || !canvasRef.current) return;
    var video = videoRef.current;
    var canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    var ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    canvas.toBlob(function (blob) {
      if (!blob) return;
      setErrorPDF(false);
      var fotoFile = new File([blob], 'foto_carta.jpg', { type: 'image/jpeg' });
      setFile(fotoFile);
      setPreviewUrl(URL.createObjectURL(blob));
      stopCamera();
      setStep('preview');
    }, 'image/jpeg', 0.9);
  }, [stopCamera]);

  var validaArchivo = function (archivo) {
    if (!archivo) return false;
    
    var nombre = archivo.name.toLowerCase();
    var tipo = (archivo.type || '').toLowerCase();
    
    // Si es PDF -> error claro
    if (tipo.indexOf('pdf') !== -1 || nombre.indexOf('.pdf') !== -1) {
      setErrorPDF(true);
      return false;
    }
    
    // Si no es imagen -> error
    if (tipo.indexOf('image/') === -1 && nombre.indexOf('.jpg') === -1 && 
        nombre.indexOf('.jpeg') === -1 && nombre.indexOf('.png') === -1 && 
        nombre.indexOf('.webp') === -1) {
      alert('Formato no soportado. Solo JPG, PNG y WebP.');
      return false;
    }
    
    setErrorPDF(false);
    return true;
  };

  var handleFileSelect = function (e) {
    var selectedFile = e.target.files[0];
    if (!selectedFile) return;
    if (!validaArchivo(selectedFile)) return;
    setFile(selectedFile);
    setPreviewUrl(URL.createObjectURL(selectedFile));
    setStep('preview');
    if (cameraActive) stopCamera();
  };

  var handleDropZone = function (e) {
    e.preventDefault();
    var droppedFile = e.dataTransfer.files[0];
    if (!droppedFile) return;
    if (!validaArchivo(droppedFile)) return;
    setFile(droppedFile);
    setPreviewUrl(URL.createObjectURL(droppedFile));
    setStep('preview');
  };

  var resetUpload = function () {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setStep('upload');
    setProductos([]);
    setErrores([]);
    setResultadoGuardado(null);
    setErrorPDF(false);
  };

  var procesarCarta = async function () {
    if (!file) return;

    if (!slug && !token) {
      setErrores(['Ingresa el slug de tu negocio para continuar.']);
      return;
    }

    setLoading(true);
    setCargandoMensaje('Enviando imagen al servidor...');
    setErrores([]);

    try {
      var formData = new FormData();
      formData.append('imagen', file);
      if (slug) formData.append('slug', slug);

      var headers = {};
      if (token) headers['Authorization'] = 'Bearer ' + token;

      setCargandoMensaje('Procesando con IA (Gemini)...');

      var controller = new AbortController();
      var timeoutId = setTimeout(function () { controller.abort(); }, 90000); // 90 segundos timeout

      var res = await fetch(API_BASE + '/api/productos/importar-carta', {
        method: 'POST',
        headers: headers,
        body: formData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      setCargandoMensaje('Interpretando respuesta...');

      // Primero leer el texto de la respuesta
      var textoRespuesta = await res.text();
      
      // Si el HTTP status no es 200, el error está en el texto
      if (res.status !== 200) {
        var mensajeError = 'Error del servidor (código ' + res.status + '). ';
        try {
          var errorData = JSON.parse(textoRespuesta);
          mensajeError = mensajeError + (errorData.message || errorData.error || textoRespuesta);
        } catch (e) {
          mensajeError = mensajeError + textoRespuesta;
        }
        setErrores([mensajeError]);
        setLoading(false);
        return;
      }

      // Si es 200, convertimos a JSON
      var data;
      try {
        data = JSON.parse(textoRespuesta);
      } catch (e) {
        setErrores(['El servidor respondió con un formato inválido. Respuesta: ' + textoRespuesta.substring(0, 200)]);
        setLoading(false);
        return;
      }

      if (data.status === 'success') {
        setProductos(data.productos || []);
        if (data.productos && data.productos.length === 0) {
          setErrores(['La IA no pudo detectar productos en esta imagen. Intenta con una foto más clara.']);
        }
        setStep('review');
      } else {
        setErrores([data.message || 'Error al procesar la imagen']);
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setErrores(['La conexión tardó demasiado. Verifica que el servidor backend esté corriendo y tenga API Key de Gemini.']);
      } else {
        setErrores(['Error de conexión con el servidor: ' + err.message + '. Verifica que el backend esté corriendo en ' + API_BASE]);
      }
    } finally {
      setLoading(false);
      setCargandoMensaje('');
    }
  };

  var updateProducto = function (index, campo, valor) {
    setProductos(function (prev) {
      return prev.map(function (p, i) {
        return i === index ? Object.assign({}, p, { [campo]: valor }) : p;
      });
    });
  };

  var removeProducto = function (index) {
    setProductos(function (prev) {
      return prev.filter(function (_, i) { return i !== index; });
    });
  };

  var guardarProductos = async function () {
    if (productos.length === 0) {
      alert('No hay productos para guardar.');
      return;
    }

    if (!slug && !token) {
      alert('Ingresa el slug de tu negocio.');
      return;
    }

    setLoading(true);
    setCargandoMensaje('Guardando productos en la base de datos...');
    try {
      var headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = 'Bearer ' + token;

      var res = await fetch(API_BASE + '/api/productos/importar-carta/guardar', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          productos: productos,
          slug: slug || undefined
        })
      });

      var data = await res.json();

      if (data.status === 'success') {
        setResultadoGuardado(data);
        setErrores([]);
        setStep('done');
      } else {
        setErrores([data.message || 'Error al guardar productos']);
      }
    } catch (err) {
      setErrores(['Error de conexión: ' + err.message]);
    } finally {
      setLoading(false);
      setCargandoMensaje('');
    }
  };

  var esPDF = function () {
    if (!file) return false;
    var nombre = file.name.toLowerCase();
    var tipo = (file.type || '').toLowerCase();
    return tipo.indexOf('pdf') !== -1 || nombre.indexOf('.pdf') !== -1;
  };

  var nombreUsuario = (usuario && usuario.nombre) || (usuario && usuario.email) || 'Usuario';

  var creados = resultadoGuardado && resultadoGuardado.creados || 0;
  var totalGuardado = resultadoGuardado && resultadoGuardado.total || 0;
  var erroresGuardado = resultadoGuardado && resultadoGuardado.errores;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f4f7f6',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
    }}>
      <style>{[
        "@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');",
        ".drop-zone {",
        "  border: 2px dashed #d1d5db;",
        "  border-radius: 20px;",
        "  padding: 40px 20px;",
        "  text-align: center;",
        "  transition: all 0.3s ease;",
        "  cursor: pointer;",
        "  background: #fafafa;",
        "}",
        ".drop-zone:hover, .drop-zone.active {",
        "  border-color: #f26b21;",
        "  background: #fff7ed;",
        "}",
        ".btn-primary-ia {",
        "  background: linear-gradient(135deg, #f26b21, #e05a1a);",
        "  color: white;",
        "  border: none;",
        "  border-radius: 16px;",
        "  padding: 16px 32px;",
        "  font-weight: 700;",
        "  font-size: 1rem;",
        "  transition: all 0.3s;",
        "  cursor: pointer;",
        "  box-shadow: 0 4px 15px rgba(242,107,33,0.3);",
        "}",
        ".btn-primary-ia:hover {",
        "  transform: translateY(-2px);",
        "  box-shadow: 0 8px 25px rgba(242,107,33,0.4);",
        "}",
        ".btn-primary-ia:disabled {",
        "  opacity: 0.6;",
        "  cursor: not-allowed;",
        "  transform: none;",
        "}",
        ".btn-secondary {",
        "  background: white;",
        "  color: #111827;",
        "  border: 2px solid #e5e7eb;",
        "  border-radius: 16px;",
        "  padding: 14px 24px;",
        "  font-weight: 600;",
        "  font-size: 0.9rem;",
        "  transition: all 0.2s;",
        "  cursor: pointer;",
        "}",
        ".btn-secondary:hover {",
        "  border-color: #f26b21;",
        "  background: #fff7ed;",
        "}",
        ".btn-success {",
        "  background: #059669;",
        "  color: white;",
        "  border: none;",
        "  border-radius: 16px;",
        "  padding: 14px 28px;",
        "  font-weight: 700;",
        "  transition: all 0.2s;",
        "  cursor: pointer;",
        "}",
        ".btn-success:hover { background: #047857; }",
        ".producto-row {",
        "  border: 1px solid #e5e7eb;",
        "  border-radius: 14px;",
        "  padding: 12px 16px;",
        "  background: white;",
        "  transition: all 0.2s;",
        "}",
        ".producto-row:hover {",
        "  border-color: #f26b21;",
        "  box-shadow: 0 2px 8px rgba(0,0,0,0.05);",
        "}",
        ".input-edit {",
        "  border: 1px solid #e5e7eb;",
        "  border-radius: 10px;",
        "  padding: 8px 12px;",
        "  font-size: 0.9rem;",
        "  width: 100%;",
        "  transition: border 0.2s;",
        "}",
        ".input-edit:focus {",
        "  outline: none;",
        "  border-color: #f26b21;",
        "  box-shadow: 0 0 0 3px rgba(242,107,33,0.1);",
        "}",
        ".pulse-loader {",
        "  display: inline-block;",
        "  width: 20px;",
        "  height: 20px;",
        "  border-radius: 50%;",
        "  border: 3px solid rgba(255,255,255,0.3);",
        "  border-top-color: white;",
        "  animation: spin 0.8s linear infinite;",
        "}",
        "@keyframes spin { to { transform: rotate(360deg); } }",
        ".card {",
        "  background: white;",
        "  border-radius: 20px;",
        "  box-shadow: 0 4px 12px rgba(0,0,0,0.05);",
        "  border: 1px solid #e5e7eb;",
        "}",
        ".step-indicator {",
        "  display: flex;",
        "  align-items: center;",
        "  justify-content: center;",
        "  gap: 8px;",
        "  margin-bottom: 24px;",
        "}",
        ".step-dot {",
        "  width: 10px;",
        "  height: 10px;",
        "  border-radius: 50%;",
        "  background: #d1d5db;",
        "  transition: all 0.3s;",
        "}",
        ".step-dot.active { background: #f26b21; width: 14px; height: 14px; }",
        ".step-dot.done { background: #059669; }",
        ".step-line {",
        "  width: 30px;",
        "  height: 2px;",
        "  background: #d1d5db;",
        "}",
        ".step-line.done { background: #059669; }"
      ].join('\n')}</style>

      <div className="container py-5" style={{ maxWidth: '800px' }}>
        {/* HEADER */}
        <div className="text-center mb-4">
          <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2"
            style={{ width: '60px', height: '60px', background: 'linear-gradient(135deg, #f26b21, #e05a1a)' }}>
            <Sparkles size={28} color="white" />
          </div>
          <h1 className="fw-bold" style={{ fontSize: '1.6rem', color: '#111827' }}>
            Importar Carta con IA
          </h1>
          <p className="text-muted" style={{ fontSize: '0.95rem' }}>
            Sube una foto de tu carta y el sistema extraerá los productos automáticamente
          </p>
          <div className="d-inline-flex align-items-center gap-2 mt-1" style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            <AlertTriangle size={12} /> Solo JPG, PNG, WebP (no acepta PDF)
          </div>
        </div>

        {/* STEP INDICATOR */}
        <div className="step-indicator">
          <div className={'step-dot ' + (step !== 'upload' ? 'done' : 'active')}></div>
          <div className={'step-line ' + (step === 'review' || step === 'done' ? 'done' : '')}></div>
          <div className={'step-dot ' + (step === 'review' ? 'active' : '') + ' ' + (step === 'done' ? 'done' : '')}></div>
          <div className={'step-line ' + (step === 'done' ? 'done' : '')}></div>
          <div className={'step-dot ' + (step === 'done' ? 'active' : '')}></div>
        </div>

        {/* ERRORES GLOBALES */}
        {errores.length > 0 && step !== 'review' && (
          <div className="alert alert-danger py-3 rounded-3 mb-3" style={{ border: '1px solid #fecaca' }}>
            <div className="fw-bold small mb-1">{'\u274C'} Error:</div>
            {errores.map(function (e, i) {
              return <div key={i} style={{ fontSize: '0.85rem' }}>{e}</div>;
            })}
            {errores.length === 1 && errores[0].indexOf('API Key') !== -1 && (
              <div className="mt-2 p-2 rounded-2" style={{ backgroundColor: '#fef2f2', fontSize: '0.8rem' }}>
                {'\uD83D\uDD11'} Para solucionarlo, agrega tu API Key de Gemini en <code>Komity_api_cli/config.json</code> como:
                <br />
                <code style={{ display: 'block', padding: '4px', backgroundColor: '#fee', marginTop: '4px' }}>{'"GEMINI_API_KEY": "AIzaSyT..."'}</code>
                <br />
                Saca la key gratis en <strong>https://aistudio.google.com/apikey</strong>
              </div>
            )}
            {errores.length === 1 && errores[0].indexOf('conexión') !== -1 && (
              <div className="mt-2 p-2 rounded-2" style={{ backgroundColor: '#fef2f2', fontSize: '0.8rem' }}>
                {'\uD83D\uDCBB'} Verifica que el backend esté corriendo. Abre una terminal y ejecuta: <code style={{ display: 'block', padding: '4px', backgroundColor: '#fee', marginTop: '4px' }}>cd Komity_api_cli {'&&'} python main.py</code>
              </div>
            )}
          </div>
        )}

        {/* STEP 1: UPLOAD */}
        {step === 'upload' && (
          <div className="card p-4 p-md-5">
            {!token && (
              <div className="mb-4 p-3 rounded-3" style={{ backgroundColor: '#fef3c7', border: '1px solid #fcd34d' }}>
                <div className="d-flex align-items-start gap-2">
                  <AlertTriangle size={18} className="text-warning flex-shrink-0 mt-1" />
                  <div>
                    <p className="fw-bold small mb-1" style={{ color: '#92400e' }}>
                      No has iniciado sesión
                    </p>
                    <p className="small mb-2" style={{ color: '#92400e' }}>
                      Ingresa el <strong>slug</strong> de tu negocio (el mismo que usas en tu QR).
                    </p>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Ej: mi-restaurante"
                      value={slug}
                      onChange={function (e) { setSlug(e.target.value.trim()); }}
                      style={{ maxWidth: '300px', borderRadius: '10px' }}
                    />
                    <p className="small mt-2 mb-0" style={{ color: '#92400e', fontSize: '0.75rem' }}>
                      {'\u00bf'}Ya tienes cuenta? <a href="/adminlogin" className="fw-bold">Inicia sesi\u00f3n</a>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {token && (
              <div className="mb-3 text-center">
                <span className="badge rounded-pill px-3 py-2" style={{ background: '#d1fae5', color: '#065f46', fontWeight: 600 }}>
                  {'\u2705'} Conectado como: {nombreUsuario}
                </span>
              </div>
            )}

            {/* ALERTA PDF */}
            {errorPDF && (
              <div className="alert alert-warning py-3 rounded-3 mb-3" style={{ border: '1px solid #fde68a' }}>
                <div className="d-flex align-items-start gap-2">
                  <AlertTriangle size={20} className="text-warning flex-shrink-0 mt-1" />
                  <div>
                    <p className="fw-bold mb-1" style={{ color: '#92400e', fontSize: '0.95rem' }}>
                      PDF no soportado
                    </p>
                    <p className="small mb-2" style={{ color: '#92400e' }}>
                      La IA de Gemini no procesa archivos PDF directamente.{' '}
                      Por favor, convierte tu carta a una imagen JPG o PNG y vuelve a subirla.
                    </p>
                    <div className="p-2 rounded-2" style={{ backgroundColor: '#fef9c3', fontSize: '0.8rem', color: '#854d0e' }}>
                      {'\uD83D\uDCF7'} Cómo convertir PDF a imagen:
                      <br />1. Abre el PDF en tu computador
                      <br />2. Haz zoom hasta que se vea bien la carta
                      <br />3. Presiona <strong>Windows + Shift + S</strong> (o Comando + Shift + 4 en Mac)
                      <br />4. Selecciona la zona de la carta
                      <br />5. Pega la captura aqu\u00ed
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* DROP ZONE */}
            <div
              className="drop-zone"
              onDragOver={function (e) { e.preventDefault(); e.currentTarget.classList.add('active'); }}
              onDragLeave={function (e) { e.currentTarget.classList.remove('active'); }}
              onDrop={handleDropZone}
              onClick={function () { if (fileInputRef.current) fileInputRef.current.click(); }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <div className="mb-3">
                <ImageIcon size={48} style={{ color: '#9ca3af' }} />
              </div>
              <h5 className="fw-bold mb-2" style={{ color: '#374151' }}>
                Arrastra tu carta aqu\u00ed o haz clic para seleccionar
              </h5>
              <p className="text-muted small mb-0">
                JPG, PNG o WebP (NO PDF)
              </p>
            </div>

            {/* OR DIVIDER */}
            <div className="d-flex align-items-center gap-3 my-4">
              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
              <span className="text-muted small fw-bold">O</span>
              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
            </div>

            {/* CAMERA BUTTON */}
            <div className="text-center">
              <button
                className="btn-secondary d-inline-flex align-items-center gap-2"
                onClick={function () {
                  if (cameraActive) stopCamera();
                  else startCamera();
                }}
              >
                <Camera size={20} />
                {cameraActive ? 'CERRAR C\u00c1MARA' : 'TOMAR FOTO CON C\u00c1MARA'}
              </button>
            </div>

            {/* CAMERA PREVIEW */}
            {cameraActive && (
              <div className="mt-4 text-center">
                <div className="position-relative d-inline-block" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    style={{ width: '100%', maxWidth: '400px', borderRadius: '16px' }}
                  />
                  <button
                    className="position-absolute btn btn-light rounded-circle shadow d-flex align-items-center justify-content-center"
                    style={{ bottom: '16px', left: '50%', transform: 'translateX(-50%)', width: '56px', height: '56px', zIndex: 10 }}
                    onClick={takePhoto}
                  >
                    <Smartphone size={24} style={{ color: '#f26b21' }} />
                  </button>
                </div>
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <p className="small text-muted mt-2">Enfoca la carta y presiona el bot\u00f3n para capturar</p>
              </div>
            )}
          </div>
        )}

        {/* STEP 2: PREVIEW */}
        {step === 'preview' && (
          <div className="card p-4 p-md-5">
            <h5 className="fw-bold mb-3">Vista previa</h5>
            
            {/* Mostrar alerta si es PDF a pesar de todo */}
            {file && esPDF() && (
              <div className="alert alert-danger py-2 small rounded-3 mb-3">
                {'\u274C'} Este archivo es PDF y no se puede procesar. {'\u00bf'}Quieres convertirla a imagen?
              </div>
            )}

            {previewUrl && (
              <div className="text-center mb-4">
                <img
                  src={previewUrl}
                  alt="Carta"
                  className="img-fluid rounded-3 shadow-sm"
                  style={{ maxHeight: '400px', objectFit: 'contain' }}
                />
              </div>
            )}
            <div className="d-flex gap-3 justify-content-center flex-wrap">
              <button className="btn-secondary d-inline-flex align-items-center gap-2" onClick={resetUpload}>
                <X size={18} /> CANCELAR
              </button>
              <button
                className="btn-primary-ia d-inline-flex align-items-center gap-2"
                onClick={procesarCarta}
                disabled={loading || (!slug && !token) || (file && esPDF())}
                title={file && esPDF() ? 'Los PDF no se pueden procesar. Convierte a imagen.' : ''}
              >
                {loading ? (
                  <><div className="pulse-loader"></div> PROCESANDO...</>
                ) : (
                  <><Sparkles size={20} /> PROCESAR CON IA</>
                )}
              </button>
            </div>
            {file && esPDF() && (
              <div className="text-center mt-2">
                <small className="text-danger fw-bold">
                  {'\u274C'} Los PDF no son compatibles. Selecciona una imagen JPG o PNG.
                </small>
              </div>
            )}
          </div>
        )}

        {/* STEP 3: REVIEW */}
        {step === 'review' && (
          <div className="card p-4 p-md-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0">
                <Check size={20} className="text-success me-1" />
                {productos.length} producto{productos.length !== 1 ? 's' : ''} detectado{productos.length !== 1 ? 's' : ''}
              </h5>
              <button className="btn btn-sm btn-outline-secondary" onClick={resetUpload}>
                <Upload size={16} className="me-1" /> Nueva carta
              </button>
            </div>

            <p className="small text-muted mb-3">
              Revisa los productos extra\u00eddos. Puedes editar cualquier campo antes de guardar.
            </p>

            {errores.length > 0 && (
              <div className="alert alert-danger py-2 small rounded-3 mb-3">
                {errores.map(function (e, i) { return <div key={i}>{'\u274C'} {e}</div>; })}
              </div>
            )}

            {/* LISTA DE PRODUCTOS */}
            <div style={{ maxHeight: '500px', overflowY: 'auto' }} className="mb-3">
              {productos.map(function (prod, idx) {
                return (
                  <div key={idx} className="producto-row mb-2">
                    <div className="d-flex align-items-start gap-2">
                      <div className="flex-grow-1">
                        <div className="d-flex gap-2 mb-2">
                          <input
                            className="input-edit fw-bold"
                            value={prod.nombre}
                            onChange={function (e) { updateProducto(idx, 'nombre', e.target.value); }}
                            placeholder="Nombre del producto"
                          />
                          <input
                            className="input-edit"
                            style={{ width: '120px', textAlign: 'right', fontWeight: 700 }}
                            value={prod.precio}
                            onChange={function (e) { updateProducto(idx, 'precio', e.target.value); }}
                            placeholder="Precio"
                          />
                        </div>
                        <div className="d-flex gap-2">
                          <input
                            className="input-edit"
                            style={{ width: '160px' }}
                            value={prod.categoria}
                            onChange={function (e) { updateProducto(idx, 'categoria', e.target.value); }}
                            placeholder="Categor\u00eda"
                          />
                          <input
                            className="input-edit"
                            value={prod.descripcion || ''}
                            onChange={function (e) { updateProducto(idx, 'descripcion', e.target.value); }}
                            placeholder="Descripci\u00f3n (opcional)"
                          />
                        </div>
                      </div>
                      <button
                        className="btn btn-sm btn-outline-danger flex-shrink-0"
                        style={{ borderRadius: '10px' }}
                        onClick={function () { removeProducto(idx); }}
                        title="Eliminar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ACCIONES */}
            <div className="d-flex gap-3 justify-content-between flex-wrap">
              <button className="btn-secondary" onClick={resetUpload}>
                <Upload size={16} className="me-1" /> Subir otra carta
              </button>
              <button
                className="btn-success d-inline-flex align-items-center gap-2 px-4"
                onClick={guardarProductos}
                disabled={loading || productos.length === 0}
              >
                {loading ? (
                  <><div className="pulse-loader"></div> GUARDANDO...</>
                ) : (
                  <><Save size={20} /> GUARDAR {productos.length} PRODUCTO{productos.length !== 1 ? 'S' : ''}</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* STEP 4: DONE */}
        {step === 'done' && (
          <div className="card p-4 p-md-5 text-center">
            <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
              style={{ width: '80px', height: '80px', background: '#d1fae5' }}>
              <Check size={40} style={{ color: '#059669' }} />
            </div>
            <h3 className="fw-bold mb-2" style={{ color: '#065f46' }}>
              {'\u00a1'}Productos creados exitosamente!
            </h3>
            <p className="text-muted mb-4">
              Se crearon <strong>{creados}</strong> de{' '}
              <strong>{totalGuardado}</strong> productos detectados.
            </p>

            {erroresGuardado && erroresGuardado.length > 0 && (
              <div className="alert alert-warning small text-start mb-4 rounded-3">
                <div className="fw-bold mb-1">{'\u26a0\ufe0f'} Algunos productos no se pudieron crear:</div>
                {erroresGuardado.map(function (err, i) {
                  return <div key={i} style={{ fontSize: '0.8rem' }}>- {err}</div>;
                })}
              </div>
            )}

            <div className="d-flex gap-3 justify-content-center flex-wrap">
              <button className="btn-secondary" onClick={resetUpload}>
                <Upload size={16} className="me-1" /> Importar otra carta
              </button>
              <a href="/productos/home/" className="btn-primary-ia d-inline-flex align-items-center gap-2 text-decoration-none px-4 py-3">
                <FileText size={20} /> IR A PRODUCTOS
              </a>
            </div>
          </div>
        )}

        {/* LOADING OVERLAY */}
        {loading && step === 'upload' && (
          <div className="text-center py-5">
            <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
              style={{ width: '70px', height: '70px', background: '#fef3c7' }}>
              <Loader size={30} className="text-warning" style={{ animation: 'spin 1s linear infinite' }} />
            </div>
            <h5 className="fw-bold">{cargandoMensaje || 'Procesando...'}</h5>
            <p className="text-muted small">Esto puede tomar hasta 30 segundos</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImportarCarta;